import React, { useState, useEffect, useRef } from "react";
import CommandMenu from "./CommandMenu";
import GradientButton from "./GradientButton";
import ActionButton from "./ActionButton";
import CameraButton from "./CameraButton";
import "./KeyboardAware.css";

// Browser-native speech-to-text (Web Speech API). Hidden where unsupported (e.g. Firefox).
const SpeechRecognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
const SPEECH_LANGS = { en: "en-US", sr: "sr-RS", ru: "ru-RU", ar: "ar-SA" };

const MicButton = ({ query, setQuery, language, className }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const filledRef = useRef(false); // has this session put text in the box yet?

  useEffect(() => () => recognitionRef.current?.abort(), []);

  // Message sent (input cleared) while listening: stop, or late results would refill the box
  useEffect(() => {
    if (listening && !query && filledRef.current) recognitionRef.current?.abort();
  }, [listening, query]);

  if (!SpeechRecognition) return null;

  const toggle = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = SPEECH_LANGS[language] || SPEECH_LANGS.en;
    recognition.interimResults = true;
    recognition.continuous = true;
    const base = query ? query.trimEnd() + " " : "";
    filledRef.current = !!base;
    recognition.onresult = (e) => {
      const spoken = Array.from(e.results, (r) => r[0].transcript).join("");
      filledRef.current = true;
      setQuery(base + spoken);
    };
    recognition.onend = recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  return (
    <button
      type="button"
      className={`${className} ${listening ? "!bg-red-500/20 !text-red-400 animate-pulse" : ""}`}
      onClick={toggle}
      aria-label={listening ? "Stop voice input" : "Ask with your voice"}
      title={listening ? "Stop voice input" : "Ask with your voice"}
    >
      <i className={`fas ${listening ? "fa-stop" : "fa-microphone"} text-[16px]`}></i>
    </button>
  );
};

const ChatInput = ({
  query,
  setQuery,
  handleSubmit,
  handleUploadClick,
  getTranslation,
  isDarkMode,
  loadingResponse,
  language = "en",
  messages = [],
  pendingFiles = [], // Add pendingFiles as a prop
}) => {
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [commandMenuActiveIndex, setCommandMenuActiveIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const textareaRef = useRef(null);
  const inputContainerRef = useRef(null);
  const isMobile = useRef(window.innerWidth < 768);
  const isSmallMobile = useRef(window.innerWidth < 375);

  // Handle resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      isMobile.current = window.innerWidth < 768;
      isSmallMobile.current = window.innerWidth < 375;
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Auto-resize textarea as user types
  const handleTextareaResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const maxHeight = isMobile.current ? 120 : 200;
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height =
        Math.min(maxHeight, scrollHeight) + "px";
    }
  };

  useEffect(() => {
    handleTextareaResize();
  }, [query]);

  const handleChange = (e) => {
    setQuery(e.target.value);
    setIsTyping(true);
    if (typingTimeout) clearTimeout(typingTimeout);
    setTypingTimeout(setTimeout(() => setIsTyping(false), 1000));
  };

  const handleKeyDown = (e) => {
    // Submit on Enter (without Shift)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      if (showCommandMenu && commands.length > 0) {
        handleSelectCommand(commands[commandMenuActiveIndex]);
        return;
      }

      if (query.trim()) {
        handleSubmit();
      }
    }
    // Handle command menu navigation
    else if (showCommandMenu) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCommandMenuActiveIndex((prev) =>
          prev < commands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCommandMenuActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        handleSelectCommand(commands[commandMenuActiveIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowCommandMenu(false);
      }
    }

    // Show command menu when slash is typed
    if (e.key === "/" && query.length === 0) {
      setShowCommandMenu(true);
      setCommandMenuActiveIndex(0);
    }
  };

  // Basic commands
  const commands = [
    { icon: "image", title: "Create an image", value: "/image " },
    { icon: "code", title: "Write code", value: "/code " },
    { icon: "map-marker-alt", title: "Make a plan", value: "/plan " },
    { icon: "comment-alt", title: "Get advice", value: "/advice " },
    { icon: "search", title: "Search", value: "/search " },
  ];

  const handleSelectCommand = (command) => {
    setQuery(command.value);
    setShowCommandMenu(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
      const length = command.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  };

  // Check input for slash commands
  useEffect(() => {
    if (query === "/") {
      setShowCommandMenu(true);
      setCommandMenuActiveIndex(0);
    } else if (query.length === 0 || query.includes(" ")) {
      setShowCommandMenu(false);
    }
  }, [query]);

  return (
    <div className="w-full relative">
      <div
        className={`flex flex-col rounded-lg overflow-hidden transition-all duration-200 mx-auto ${
          isDarkMode
            ? "bg-[#1A1A1A] border border-gray-700/30"
            : "bg-white border border-gray-200/70"
        } ${isFocused ? "shadow-lg" : "shadow"}`}
      >
        {/* Command Menu */}
        {showCommandMenu && (
          <div
            className={`absolute bottom-full left-0 right-0 mb-1 p-2 rounded-lg shadow-lg z-10 ${
              isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <p className="px-3 py-1 text-xs opacity-60">Commands</p>
            <div className="max-h-60 overflow-y-auto">
              {commands.map((command, index) => (
                <div
                  key={index}
                  onClick={() => handleSelectCommand(command)}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer rounded ${
                    index === commandMenuActiveIndex
                      ? isDarkMode
                        ? "bg-gray-700"
                        : "bg-gray-100"
                      : ""
                  }`}
                >
                  <div
                    className={`h-6 w-6 rounded-full flex items-center justify-center ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-100"
                    }`}
                  >
                    <i
                      className={`fas fa-${command.icon} text-xs ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    ></i>
                  </div>
                  <div>
                    <p className="font-medium">{command.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mobile: Input on top, buttons on bottom */}
        {isMobile.current ? (
          <div className="flex flex-col">
            {/* Text input area on top */}
            <div className="p-2">
              <textarea
                ref={textareaRef}
                name="query"
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={getTranslation("Message ChatGPT...")}
                className={`w-full py-3 px-4 rounded-2xl resize-none outline-none ${
                  isDarkMode
                    ? "bg-[#181c20] border border-gray-700/30 text-white placeholder-gray-400"
                    : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500"
                }`}
                style={{
                  minHeight: "50px",
                  maxHeight: "200px",
                  direction: language === "ar" ? "rtl" : "ltr",
                }}
                rows="1"
              />
            </div>

            {/* Buttons on bottom */}
            <div className="flex items-center justify-between p-2 pt-0 gap-2">
              <div className="flex items-center gap-2">
                {/* Upload Button */}
                <button
                  className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors ${
                    isDarkMode
                      ? "bg-[#181c20] hover:bg-gray-700 text-gray-400"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-500"
                  }`}
                  onClick={() => handleUploadClick()}
                  aria-label="Attach files"
                >
                  <i className="fas fa-paperclip text-[16px]"></i>
                </button>

                <MicButton
                  query={query}
                  setQuery={setQuery}
                  language={language}
                  className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors ${
                    isDarkMode
                      ? "bg-[#181c20] hover:bg-gray-700 text-gray-400"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-500"
                  }`}
                />
              </div>

              {/* Send button */}
              <button
                onClick={handleSubmit}
                disabled={
                  (!query.trim() && pendingFiles.length === 0) ||
                  loadingResponse
                }
                className={`p-3 rounded-full flex-shrink-0 ${
                  (query.trim() || pendingFiles.length > 0) && !loadingResponse
                    ? "bg-green-500 hover:bg-green-600 text-white"
                    : isDarkMode
                    ? "bg-gray-800 text-gray-500"
                    : "bg-gray-200 text-gray-400"
                } transition-colors w-11 h-11 flex items-center justify-center`}
                aria-label="Send message"
              >
                {loadingResponse ? (
                  <i className="fas fa-circle-notch fa-spin text-sm"></i>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    className={`w-5 h-5 ${
                      isDarkMode && query.trim() ? "text-black" : ""
                    }`}
                    fill="currentColor"
                  >
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Desktop: Original horizontal layout */
          <div className="relative flex items-end p-2 gap-2">
            {/* Upload Button - moved before textarea */}
            <button
              className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors mb-1 ${
                isDarkMode
                  ? "bg-[#181c20] hover:bg-gray-700 text-gray-400"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-500"
              }`}
              onClick={() => handleUploadClick()}
              aria-label="Attach files"
            >
              <i className="fas fa-paperclip text-[16px]"></i>
            </button>

            <MicButton
              query={query}
              setQuery={setQuery}
              language={language}
              className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors mb-1 ${
                isDarkMode
                  ? "bg-[#181c20] hover:bg-gray-700 text-gray-400"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-500"
              }`}
            />

            <div className="flex-grow relative">
              <textarea
                ref={textareaRef}
                name="query"
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={getTranslation("Message ChatGPT...")}
                className={`w-full py-3 px-4 rounded-2xl resize-none outline-none ${
                  isDarkMode
                    ? "bg-[#181c20] border border-gray-700/30 text-white placeholder-gray-400"
                    : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500"
                }`}
                style={{
                  minHeight: "50px",
                  maxHeight: "200px",
                  direction: language === "ar" ? "rtl" : "ltr",
                }}
                rows="1"
              />
            </div>

            {/* Send button */}
            <button
              onClick={handleSubmit}
              disabled={
                (!query.trim() && pendingFiles.length === 0) || loadingResponse
              }
              className={`p-3 rounded-full flex-shrink-0 ${
                (query.trim() || pendingFiles.length > 0) && !loadingResponse
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : isDarkMode
                  ? "bg-gray-800 text-gray-500"
                  : "bg-gray-200 text-gray-400"
              } transition-colors w-11 h-11 flex items-center justify-center mb-1`}
              aria-label="Send message"
            >
              {loadingResponse ? (
                <i className="fas fa-circle-notch fa-spin text-sm"></i>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  className={`w-5 h-5 ${
                    isDarkMode && query.trim() ? "text-black" : ""
                  }`}
                  fill="currentColor"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              )}
            </button>
          </div>
        )}

        {/* Bottom action bar - commented out as requested */}
        {/* <div className={`p-2 flex justify-between items-center ${
          isDarkMode ? "bg-[#1a1a1a] border-t border-gray-800" : "bg-gray-50 border-t border-gray-100"
        }`}>
          <div className="flex items-center gap-2">
            
          </div>
          
          <button 
            onClick={() => query.trim() && handleSubmit()}
            disabled={!query.trim() || loadingResponse}
            className={`p-3 rounded-full ${
              query.trim() && !loadingResponse
                ? "bg-green-500 hover:bg-green-600 text-white"
                : isDarkMode
                  ? "bg-gray-800 text-gray-500"
                  : "bg-gray-200 text-gray-400"
            } transition-colors w-12 h-12 flex items-center justify-center`}
            aria-label="Send message"
          >
            {loadingResponse ? (
              <i className="fas fa-circle-notch fa-spin text-base"></i>
            ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24"
                className={`w-6 h-6 ${isDarkMode && query.trim() ? "text-black" : ""}`}
                fill="currentColor"
              >
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default ChatInput;
